import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Divider,
  Progress,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Spinner,
  RadioGroup,
  Radio,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import { Settlement, Species } from 'common/src/utils/types';
import { supabase } from 'common/src/supabase';
import useUserStore from '../../../../common/src/stores/useUserStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import useDataStore from '../../../../common/src/stores/useDataStore';
import { SEASONS } from 'common/src/utils/definitions';
import { getSettlementsByExporterId } from 'common/src/api/settlements';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import {
  checkManualSettlementCosts,
  OfficialCostCategory,
  recordManualSettlementCosts,
} from 'common/src/api/receiver-costs';

const COST_TYPES = [
  {
    value: 'commission',
    label: 'Comisión',
    field: 'local_total_commision',
    totalField: 'total_commission_local',
    currency: 'local',
    distribution: 'bySales' as const,
  },
  {
    value: 'tax',
    label: 'Tax',
    field: 'local_total_tax',
    totalField: 'total_tax_local',
    currency: 'local',
    distribution: 'bySales' as const,
  },
  {
    value: 'repacking',
    label: 'Repacking',
    field: 'local_repacking_costs',
    totalField: 'total_repacking_costs_local',
    currency: 'local',
    distribution: 'byKg' as const,
  },
  {
    value: 'other_costs',
    label: 'Otros Costos',
    field: 'local_other_costs',
    totalField: 'total_other_costs_local',
    currency: 'local',
    distribution: 'byKg' as const,
  },
  {
    value: 'freight',
    label: 'Flete',
    field: 'usd_freight',
    totalField: 'total_freight_usd',
    currency: 'usd',
    distribution: 'byKg' as const,
  },
];

type UploadMode = 'add' | 'replace';

interface ExcelRow {
  reference_number: string;
  amount: number;
}

interface ValidationError {
  row: number;
  message: string;
}

interface ProcessedRow extends ExcelRow {
  rowIndex: number;
  settlement?: Settlement;
  currentAmount: number;
}

interface MassiveCostsUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const MassiveCostsUploadModal: React.FC<MassiveCostsUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('settlements');
  const { userProfile } = useUserStore();
  const { selectedSeason: globalSeason } = useFilterStore();
  const { species, selectedSpecies: selectedSpeciesFromStore } = useDataStore();

  const [season, setSeason] = useState<string>(globalSeason || '');
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(
    selectedSpeciesFromStore ?? null
  );

  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [loadingSettlements, setLoadingSettlements] = useState(false);

  const [step, setStep] = useState<'config' | 'review' | 'uploading'>('config');
  const [costType, setCostType] = useState<string>('');
  const [uploadMode, setUploadMode] = useState<UploadMode>('replace');
  const [file, setFile] = useState<File | null>(null);
  const [processedRows, setProcessedRows] = useState<ProcessedRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const selectedCostType = useMemo(
    () => COST_TYPES.find((ct) => ct.value === costType),
    [costType]
  );

  useEffect(() => {
    if (isOpen) {
      if (!season && globalSeason) setSeason(globalSeason);
      if (!selectedSpecies && selectedSpeciesFromStore)
        setSelectedSpecies(selectedSpeciesFromStore);
    }
  }, [isOpen, globalSeason, selectedSpeciesFromStore]);

  useEffect(() => {
    if (!isOpen || !season || !selectedSpecies || !userProfile?.exporter_id)
      return;

    let cancelled = false;
    const fetchData = async () => {
      setLoadingSettlements(true);
      try {
        const data = await getSettlementsByExporterId(
          userProfile.exporter_id,
          undefined,
          season,
          selectedSpecies.id
        );
        if (!cancelled) setSettlements(data || []);
      } catch (error) {
        console.error('Error fetching settlements:', error);
        if (!cancelled) setSettlements([]);
      } finally {
        if (!cancelled) setLoadingSettlements(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [isOpen, season, selectedSpecies, userProfile?.exporter_id]);

  const settlementsByRef = useMemo(() => {
    const map = new Map<string, Settlement>();
    settlements.forEach((s) => {
      if (s.reference_number) {
        map.set(s.reference_number.trim().toUpperCase(), s);
      }
    });
    return map;
  }, [settlements]);

  const resetState = useCallback(() => {
    setStep('config');
    setCostType('');
    setUploadMode('replace');
    setFile(null);
    setProcessedRows([]);
    setValidationErrors([]);
    setProgress(0);
    setIsProcessing(false);
    setSeason(globalSeason || '');
    setSelectedSpecies(selectedSpeciesFromStore ?? null);
    setSettlements([]);
  }, [globalSeason, selectedSpeciesFromStore]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleDownloadTemplate = useCallback(() => {
    if (!selectedCostType) return;

    const amountLabel =
      selectedCostType.currency === 'usd' ? 'Monto USD' : 'Monto Moneda Local';

    const headers = ['Número de Referencia', amountLabel];
    const exampleRow = ['REF-001', selectedCostType.currency === 'usd' ? 50 : 1000];
    const colWidths = [{ wch: 22 }, { wch: 20 }];

    const wsData = [headers, exampleRow];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Costos');
    XLSX.writeFile(wb, `plantilla_costos_${selectedCostType.value}.xlsx`);
  }, [selectedCostType]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0] || null;
      setFile(selectedFile);
    },
    []
  );

  const processFile = useCallback(async () => {
    if (!file || !selectedCostType) return;
    setIsProcessing(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet);

      const errors: ValidationError[] = [];
      const rows: ProcessedRow[] = [];

      jsonData.forEach((row, idx) => {
        const rowIndex = idx + 2;

        const rawRef =
          row['Número de Referencia'] ?? row['Numero de Referencia'] ?? '';
        const reference_number = String(rawRef).trim();
        if (!reference_number) {
          errors.push({
            row: rowIndex,
            message: t('Falta Número de Referencia'),
          });
          return;
        }

        const settlement = settlementsByRef.get(reference_number.toUpperCase());
        if (!settlement) {
          errors.push({
            row: rowIndex,
            message: `${reference_number}: ${t('Liquidación no encontrada')}`,
          });
          return;
        }

        if (
          !settlement.settlement_details ||
          settlement.settlement_details.length === 0
        ) {
          errors.push({
            row: rowIndex,
            message: `${reference_number}: ${t('Liquidación sin detalles')}`,
          });
          return;
        }

        const amountKey =
          selectedCostType.currency === 'usd'
            ? (row['Monto USD'] ?? row['Monto usd'] ?? row['monto usd'] ?? 0)
            : (row['Monto Moneda Local'] ?? row['Monto moneda local'] ?? row['monto moneda local'] ?? 0);

        const amount = Number(amountKey);
        if (isNaN(amount)) {
          errors.push({
            row: rowIndex,
            message: `${reference_number}: ${t('Monto inválido')}`,
          });
          return;
        }

        const currentAmount =
          (settlement as any)[selectedCostType.totalField] || 0;

        rows.push({
          rowIndex,
          reference_number,
          amount,
          settlement,
          currentAmount,
        });
      });

      setValidationErrors(errors);
      setProcessedRows(rows);
      setStep('review');
    } catch (error) {
      console.error('Error processing file:', error);
      toast.error(t('Error al procesar el archivo'));
    } finally {
      setIsProcessing(false);
    }
  }, [file, selectedCostType, settlementsByRef, t]);

  const handleUpload = useCallback(async () => {
    if (processedRows.length === 0 || !selectedCostType) return;
    setStep('uploading');
    setProgress(0);

    try {
      // Refuse the whole file before writing anything, so it is never
      // half-applied.
      const refused = await checkManualSettlementCosts(
        processedRows.map((row) => row.settlement!.id),
        selectedCostType.value as OfficialCostCategory,
        uploadMode
      );
      if (refused.length > 0) {
        const referenceOf = (id: number) =>
          processedRows.find((row) => row.settlement!.id === id)
            ?.reference_number || String(id);
        const shared = refused
          .filter((item) => item.reason === 'shared')
          .map((item) => referenceOf(item.settlementId));
        const blocked = refused
          .filter((item) => item.reason === 'blocked')
          .map((item) => referenceOf(item.settlementId));
        toast.error(
          [
            shared.length > 0 &&
              `${t('Costos multi-despacho, usa Agregar')}: ${shared.join(', ')}`,
            blocked.length > 0 &&
              `${t('Liquidaciones bloqueadas')}: ${blocked.join(', ')}`,
          ]
            .filter(Boolean)
            .join('. '),
          { autoClose: false }
        );
        setStep('review');
        return;
      }

      const total = processedRows.length;
      let processed = 0;

      for (const row of processedRows) {
        const settlement = row.settlement!;
        const details = settlement.settlement_details || [];
        if (details.length === 0) continue;

        const targetAmount =
          uploadMode === 'add' ? row.currentAmount + row.amount : row.amount;

        // Keep the raw cost lines (and the exporter cost view) in step.
        // Settlements without an official receiver cannot hold cost lines.
        // Replace with 0 still runs, so it clears the settlement's own lines.
        if (
          settlement.exporter_receiver_id &&
          (row.amount > 0 || uploadMode === 'replace')
        ) {
          await recordManualSettlementCosts(
            settlement.id,
            [
              {
                name: `${selectedCostType.label} (carga masiva)`,
                amount: row.amount,
                currency:
                  selectedCostType.currency === 'usd'
                    ? 'USD'
                    : settlement.local_currency || 'USD',
                officialCategory:
                  selectedCostType.value as OfficialCostCategory,
              },
            ],
            uploadMode
          );
        }

        const exchangeRate = settlement.exchange_rate || 1;
        const totalKilos = details.reduce((sum, d) => sum + (d.kilos || 0), 0);
        const totalSales = details.reduce(
          (sum, d) => sum + (d.local_total_price || 0),
          0
        );

        // Build updated details with distributed cost + recalculated FOB
        const updatedDetails = details.map((detail) => {
          const proportion =
            selectedCostType.distribution === 'byKg'
              ? totalKilos > 0
                ? (detail.kilos || 0) / totalKilos
                : 0
              : totalSales > 0
                ? (detail.local_total_price || 0) / totalSales
                : 0;

          const distributedAmount = targetAmount * proportion;

          // Build the updated cost values for this detail
          const newCosts = {
            local_total_commision:
              selectedCostType.value === 'commission'
                ? distributedAmount
                : detail.local_total_commision || 0,
            local_total_tax:
              selectedCostType.value === 'tax'
                ? distributedAmount
                : detail.local_total_tax || 0,
            local_other_costs:
              selectedCostType.value === 'other_costs'
                ? distributedAmount
                : detail.local_other_costs || 0,
            local_repacking_costs:
              selectedCostType.value === 'repacking'
                ? distributedAmount
                : detail.local_repacking_costs || 0,
            usd_freight:
              selectedCostType.value === 'freight'
                ? distributedAmount
                : detail.usd_freight || 0,
          };

          const usd_total_fob =
            ((detail.local_total_price || 0) -
              newCosts.local_total_tax -
              newCosts.local_total_commision -
              newCosts.local_other_costs -
              newCosts.local_repacking_costs) /
              exchangeRate -
            newCosts.usd_freight;

          return {
            id: detail.id,
            [selectedCostType.field]: distributedAmount,
            usd_total_fob,
          };
        });

        // Apply floating-point remainder correction on the last detail
        if (updatedDetails.length > 0) {
          const sum = updatedDetails.reduce(
            (acc, d) => acc + ((d as any)[selectedCostType.field] || 0),
            0
          );
          const remainder = targetAmount - sum;
          if (Math.abs(remainder) > 0.000001) {
            const last = updatedDetails[updatedDetails.length - 1];
            (last as any)[selectedCostType.field] =
              ((last as any)[selectedCostType.field] || 0) + remainder;
          }

          // Recalculate FOB on the last detail after remainder correction
          const lastDetail = details[details.length - 1];
          const lastUpdated = updatedDetails[updatedDetails.length - 1];
          const lastCosts = {
            local_total_commision:
              selectedCostType.value === 'commission'
                ? (lastUpdated as any)[selectedCostType.field]
                : lastDetail.local_total_commision || 0,
            local_total_tax:
              selectedCostType.value === 'tax'
                ? (lastUpdated as any)[selectedCostType.field]
                : lastDetail.local_total_tax || 0,
            local_other_costs:
              selectedCostType.value === 'other_costs'
                ? (lastUpdated as any)[selectedCostType.field]
                : lastDetail.local_other_costs || 0,
            local_repacking_costs:
              selectedCostType.value === 'repacking'
                ? (lastUpdated as any)[selectedCostType.field]
                : lastDetail.local_repacking_costs || 0,
            usd_freight:
              selectedCostType.value === 'freight'
                ? (lastUpdated as any)[selectedCostType.field]
                : lastDetail.usd_freight || 0,
          };
          lastUpdated.usd_total_fob =
            ((lastDetail.local_total_price || 0) -
              lastCosts.local_total_tax -
              lastCosts.local_total_commision -
              lastCosts.local_other_costs -
              lastCosts.local_repacking_costs) /
              exchangeRate -
            lastCosts.usd_freight;
        }

        // Persist all detail updates
        for (const upd of updatedDetails) {
          const { error } = await supabase
            .from('settlements_details')
            .update({
              [selectedCostType.field]: (upd as any)[selectedCostType.field],
              usd_total_fob: upd.usd_total_fob,
            })
            .eq('id', upd.id);

          if (error) throw error;
        }

        // Update the settlement-level total
        const { error: settlementError } = await supabase
          .from('settlements')
          .update({ [selectedCostType.totalField]: targetAmount })
          .eq('id', settlement.id);

        if (settlementError) throw settlementError;

        processed += 1;
        setProgress(Math.round((processed / total) * 100));
      }

      toast.success(
        `${t('Se procesaron')} ${processed} ${t('liquidaciones exitosamente')}`
      );
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error uploading costs:', error);
      toast.error(error?.message || t('Error al subir los costos'));
      setStep('review');
    }
  }, [processedRows, selectedCostType, uploadMode, onSuccess, handleClose, t]);

  const canProcess =
    file &&
    costType &&
    season &&
    selectedSpecies &&
    !loadingSettlements &&
    settlements.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size='3xl'
      scrollBehavior='inside'
    >
      <ModalContent>
        <ModalHeader className='flex flex-col gap-1'>
          <span>
            {t('Subida Masiva')} - {t('Costos')}
          </span>
          {step !== 'config' && selectedCostType && (
            <span className='text-sm text-default-500 font-normal'>
              {selectedSpecies?.name} | {season} | {t(selectedCostType.label)} |{' '}
              {selectedCostType.distribution === 'byKg'
                ? t('Por Kg')
                : t('Por Venta')}{' '}
              | {selectedCostType.currency === 'usd' ? 'USD' : t('Moneda Local')}
            </span>
          )}
        </ModalHeader>
        <ModalBody>
          {step === 'config' && (
            <div className='flex flex-col gap-4'>
              <div className='grid grid-cols-2 gap-4'>
                <Select
                  label={t('Temporada') + ' *'}
                  selectedKeys={season ? [season] : []}
                  onChange={(e) => setSeason(e.target.value)}
                  size='sm'
                >
                  {Object.values(SEASONS).map((s) => (
                    <SelectItem key={s.name} value={s.name}>
                      {s.name}
                    </SelectItem>
                  ))}
                </Select>

                <Select
                  label={t('Especie') + ' *'}
                  selectedKeys={
                    selectedSpecies ? [selectedSpecies.id.toString()] : []
                  }
                  onChange={(e) => {
                    const sp = species.find(
                      (sp) => sp.id.toString() === e.target.value
                    );
                    setSelectedSpecies(sp || null);
                  }}
                  size='sm'
                >
                  {species.map((sp) => (
                    <SelectItem key={sp.id} value={sp.id.toString()}>
                      {sp.name}
                    </SelectItem>
                  ))}
                </Select>
              </div>

              {loadingSettlements && (
                <div className='flex items-center gap-2 text-default-500 text-sm'>
                  <Spinner size='sm' />
                  {t('Cargando liquidaciones...')}
                </div>
              )}

              {!loadingSettlements && season && selectedSpecies && (
                <Chip
                  size='sm'
                  variant='flat'
                  color={settlements.length > 0 ? 'success' : 'warning'}
                >
                  {settlements.length} {t('liquidaciones encontradas')}
                </Chip>
              )}

              <Divider />

              <Select
                label={t('Tipo de Costo') + ' *'}
                placeholder={t('Selecciona el tipo')}
                selectedKeys={costType ? [costType] : []}
                onSelectionChange={(keys) => {
                  const val = Array.from(keys)[0] as string;
                  setCostType(val || '');
                }}
              >
                {COST_TYPES.map((ct) => (
                  <SelectItem key={ct.value} value={ct.value}>
                    {t(ct.label)}
                  </SelectItem>
                ))}
              </Select>

              <RadioGroup
                label={t('Modo de carga')}
                orientation='horizontal'
                value={uploadMode}
                onValueChange={(val) => {
                  if (val === 'add' || val === 'replace') setUploadMode(val);
                }}
              >
                <Radio
                  value='replace'
                  description={t('El monto reemplaza el total actual')}
                >
                  {t('Reemplazar')}
                </Radio>
                <Radio
                  value='add'
                  description={t('El monto se suma al total actual')}
                >
                  {t('Agregar')}
                </Radio>
              </RadioGroup>

              {selectedCostType && (
                <div className='flex gap-2 flex-wrap'>
                  <Chip size='sm' variant='flat'>
                    {selectedCostType.distribution === 'byKg'
                      ? t('Distribución por Kg')
                      : t('Distribución por Venta')}
                  </Chip>
                  <Chip size='sm' variant='flat'>
                    {selectedCostType.currency === 'usd'
                      ? t('Moneda: USD')
                      : t('Moneda: Local')}
                  </Chip>
                </div>
              )}

              <Divider />

              <div className='flex flex-col gap-2'>
                <Button
                  variant='flat'
                  startContent={<Icon icon='tabler:download' />}
                  onClick={handleDownloadTemplate}
                  size='sm'
                  isDisabled={!costType}
                >
                  {t('Descargar Plantilla')}
                </Button>

                <div className='flex items-center gap-2'>
                  <input
                    type='file'
                    accept='.xlsx,.xls,.csv'
                    onChange={handleFileChange}
                    className='block w-full text-sm text-default-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-primary-50 file:text-primary-700
                      hover:file:bg-primary-100'
                  />
                </div>
              </div>
            </div>
          )}

          {step === 'review' && (
            <div className='flex flex-col gap-4'>
              <div className='flex gap-2 flex-wrap'>
                <Chip color='success' variant='flat'>
                  {processedRows.length} {t('filas válidas')}
                </Chip>
                {validationErrors.length > 0 && (
                  <Chip color='danger' variant='flat'>
                    {validationErrors.length} {t('errores')}
                  </Chip>
                )}
              </div>

              {validationErrors.length > 0 && (
                <div className='max-h-40 overflow-y-auto'>
                  <Table aria-label='Validation errors' isCompact removeWrapper>
                    <TableHeader>
                      <TableColumn>{t('Fila')}</TableColumn>
                      <TableColumn>{t('Error')}</TableColumn>
                    </TableHeader>
                    <TableBody>
                      {validationErrors.map((err, i) => (
                        <TableRow key={i}>
                          <TableCell>{err.row}</TableCell>
                          <TableCell className='text-danger'>
                            {err.message}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {processedRows.length > 0 && (
                <>
                  <Divider />
                  <div className='max-h-60 overflow-y-auto'>
                    <Table aria-label='Preview data' isCompact removeWrapper>
                      <TableHeader>
                        <TableColumn>{t('Referencia')}</TableColumn>
                        <TableColumn>{t('Actual')}</TableColumn>
                        <TableColumn>{t('Nuevo')}</TableColumn>
                        <TableColumn>{t('Diferencia')}</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {processedRows.map((row, i) => {
                          const newAmount =
                            uploadMode === 'add'
                              ? row.currentAmount + row.amount
                              : row.amount;
                          const diff = newAmount - row.currentAmount;
                          const locale =
                            selectedCostType?.currency === 'usd' ? 'en-US' : 'es-CL';
                          const fmt = (v: number) =>
                            v.toLocaleString(locale, {
                              minimumFractionDigits: 2,
                            });
                          return (
                            <TableRow key={i}>
                              <TableCell>{row.reference_number}</TableCell>
                              <TableCell>{fmt(row.currentAmount)}</TableCell>
                              <TableCell className='font-semibold'>
                                {fmt(newAmount)}
                              </TableCell>
                              <TableCell
                                className={
                                  diff > 0
                                    ? 'text-danger'
                                    : diff < 0
                                      ? 'text-success'
                                      : 'text-default-400'
                                }
                              >
                                {diff > 0 ? '+' : ''}
                                {fmt(diff)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </div>
          )}

          {step === 'uploading' && (
            <div className='flex flex-col gap-4 py-8'>
              <Progress
                aria-label='Upload progress'
                value={progress}
                color='primary'
                showValueLabel
                className='max-w-full'
              />
              <p className='text-center text-default-500'>
                {t('Procesando costos...')}
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {step === 'config' && (
            <>
              <Button variant='light' onPress={handleClose}>
                {t('Cancelar')}
              </Button>
              <Button
                color='primary'
                onPress={processFile}
                isDisabled={!canProcess}
                isLoading={isProcessing}
                startContent={<Icon icon='tabler:file-spreadsheet' />}
              >
                {t('Procesar Archivo')}
              </Button>
            </>
          )}
          {step === 'review' && (
            <>
              <Button variant='light' onPress={() => setStep('config')}>
                {t('Volver')}
              </Button>
              <Button
                color='primary'
                onPress={handleUpload}
                isDisabled={processedRows.length === 0}
                startContent={<Icon icon='tabler:upload' />}
              >
                {t('Subir')} {processedRows.length} {t('costos')}
              </Button>
            </>
          )}
          {step === 'uploading' && (
            <Button variant='flat' isDisabled>
              {t('Procesando...')}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MassiveCostsUploadModal;
